import { useMemo } from 'react';

import { keepPreviousData } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS } from 'cpool-constants';
import { schemaLoanRequestResolver_GetRecordedDocuments } from 'cpool-tables/backend-resolvers';

import type { SchemaRecordedDocumentListEntry } from '~/features/leadDetails/schemas/recordedDocuments.schema';
import { useAggregateQuery } from '~/queryUtils/useQuery';
import { useQueryPositionStore } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys.ts';

const useRecordedDocuments = (id: string) => {
    const queryKey = queryKeys.getRecordedDocuments(id);
    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(queryKey)]);
    const recordedDocuments = useAggregateQuery({
        acl: { loanRequestId: id },
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getRecordedDocuments,
        schema: schemaLoanRequestResolver_GetRecordedDocuments,
        searchParams: { id, position: queryPosition },
        tanstack: {
            placeholderData: keepPreviousData,
        },
    });

    const documents = useMemo(
        () =>
            recordedDocuments.data?.documents.map(
                (document): SchemaRecordedDocumentListEntry => ({
                    attribution: document.attribution,
                    confirmedDate: document.acceptedDate ? new Date(document.acceptedDate) : null,
                    fileFormat: document.fileExtension,
                    fileName: document.name,
                    key: document.key,
                    mimeType: document.mimeType,
                    uploadDate: new Date(document.date),
                    uploadedTo: document.uploadedTo,
                }),
            ) ?? [],
        [recordedDocuments.data?.documents],
    );

    return { documents, isError: recordedDocuments.isError, isLoading: recordedDocuments.isLoading };
};

export default useRecordedDocuments;
